import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';

import { AdStudioContainerStore, AdStudioContainerState } from './ad-container.store';
import { CreativeUnitPackageQuery } from './ad-unit-package.query';
import { CartQuery } from '../cart/cart.query';
import { LayerDto } from '../../../../../../api/src/creative-unit-package/models/layer/layer.dto';
import { CreativeUnitWithCustomizationsDto } from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';
import { createLayoutGroupDefaults, LayoutGroupModel, LayoutMode, normalizeLayoutGroup } from '../../../../../../api/src/creative-unit-package/models/layer/layout-group.dto';
import { LayersUtils } from '../../../../../../api/src/creative-unit-package/utils/layers.utils';
import { CreativeUnitUtils } from '../../../../../../api/src/creative-unit-package/utils/creative-unit.utils';

@Injectable({ providedIn: 'root' })
export class AdStudioContainerQuery extends Query<AdStudioContainerState> {
	preview$ = this.select(state => state.preview);
	interactions$ = this.select(state => state.interactions);
	customizationsEditMode$ = this.select(state => state.customizationsEditMode);

	hoveredLayer$ = this.select(state => state.interactions.hoveredLayer);
	selectedLayers$ = this.select(state => state.interactions.selectedLayers);
	inlineEditedLayer$ = this.select(state => state.interactions.inlineEditedLayer);

	historyState$ = this.select(state => state.changeHistoryState);
	sidebar$ = this.select(state => state.sidebar);
	animationEditorMode$ = this.select(state => state.sidebar.animationEditorMode);
	cropMode$ = this.select(state => state.cropMode);

	/** Observable for comment context (layer-linked and timestamp-linked comments) */
	commentContext$ = this.select(state => state.commentContext);

	/** Observable for current app context (editor, preview, review) */
	appContext$ = this.select(state => state.appContext);

	constructor(
		protected override readonly store: AdStudioContainerStore,
		private readonly creativeUnitPackageQuery: CreativeUnitPackageQuery,
		private readonly cartQuery: CartQuery
	) {
		super(store);
	}

	getHoveredLayer() {
		return this.getValue().interactions.hoveredLayer;
	}

	getSelectedLayers() {
		return this.getValue().interactions.selectedLayers;
	}

	getInteractionUnitId() {
		return this.getValue().interactionUnitId;
	}

	getEnableInteractions() {
		return this.getValue().interactions.enableInteractions;
	}

	getTemplateIncludesInteractions() {
		return this.getValue().interactions.templateIncludesInteractions;
	}

	getSelectedGroupId() {
		return this.getValue().selectedGroupId;
	}

	getMergeTagState() {
		return this.getValue().mergeTagState;
	}

	getInteractionState() {
		return this.getValue().interactions;
	}

	isLassoActive() {
		return this.getValue().interactions.isLassoActive || false;
	}

	selectIsLassoActive() {
		return this.select(state => state.interactions.isLassoActive);
	}

	getPreviewState() {
		return this.getValue().preview;
	}

	getCustomizationOptionsById(id: string) {
		return this.getValue().customizations?.options?.[id];
	}

	getCustomizationOptionCacheByHash(hash: string) {
		return this.getValue().customizations?.optionCache?.[hash];
	}

	getCustomizationPendingFetchByHash(hash: string) {
		return this.getValue().customizations?.pendingFetchMap?.[hash];
	}

	getSidebarState() {
		return this.getValue().sidebar.sidebarState;
	}

	getAppContext() {
		return this.getValue().appContext;
	}

	getCustomizationMode() {
		return !!this.getValue().customizationsEditMode;
	}
	getPreviewContext() {
		return this.getValue().previewContext;
	}

	getSelectedCreativeUnits() {
		let selected = this.getValue().selectedUnits;
		let cartItems = this.cartQuery.getItemsFromSamePackage(this.creativeUnitPackageQuery.getActive()?.id);

		return cartItems?.filter(cu => selected.some(scu => scu === cu.id)) || [];
	}

	/**
	 * Gets the active creative unit data.
	 * If the app context is 'editor', it will return the active creative unit preview.
	 * If the app context is 'preview', it will return the active item from the cart's item data.
	 */
	getActivePreview() {
		if (this.getValue().appContext === 'editor') {
			const activeCreativeUnitId = this.creativeUnitPackageQuery.getActiveCreativeUnitId();
			return this.getValue().preview.creativeUnitPreviews.find(cu => cu.id === activeCreativeUnitId);
		} else {
			// User customization side
			const activeId = this.cartQuery.getValue().editor.activeId;
			return CreativeUnitUtils.findCreativeUnitById(this.getValue().preview.creativeUnitPreviews, activeId);
		}
	}

	getPreviewById(id: string) {
		return this.getValue().preview.creativeUnitPreviews.find(cu => cu.id === id);
	}

	getActivePreviewElement() {
		return this.getValue().preview.creativeUnitPreviewElementRefs[this.getActivePreview()?.id];
	}

	getPreviewElementRef(creativeUnitId?: string) {
		const previewRefs = this.getValue().preview.creativeUnitPreviewElementRefs;

		if (!previewRefs) {
			return;
		}

		// If we have a specific creative unit ID, use it
		if (creativeUnitId) {
			return previewRefs[creativeUnitId];
		}

		// Otherwise, if on "All" tab (no activeCreativeUnit), use the first available ref
		const firstRefId = Object.keys(previewRefs)[0];
		if (firstRefId) {
			return previewRefs[firstRefId];
		}

		return;
	}

	/**
	 * Gets the DOM element of the active layer from the active creative unit.
	 */
	getActiveLayerElement() {
		let element = this.getValue().interactions.selectedLayers[0]?.element;

		if (!element) {
			let activeItem = this.getActivePreview();
			element = this.getValue().preview.creativeUnitPreviewElementRefs[activeItem.id];
		}

		return element;
	}

	/**
	 * Get selected layers from the actual active preview
	 */
	getSelectedLayersFromActivePreview() {
		const activePreview = this.getActivePreview();
		return this.getSelectedLayersFromUnitPreview(activePreview);
	}

	getSelectedLayersFromUnitPreview(activePreview: CreativeUnitWithCustomizationsDto) {
		const selectedLayers = this.getValue().interactions.selectedLayers;

		if (!activePreview || !selectedLayers?.length) {
			return [];
		}

		function findSelectedLayers(layers: LayerDto[]) {
			let result = [];
			layers?.forEach((layer: LayerDto) => {
				const selectedLayer = selectedLayers.find(sl => sl.id === layer.id);

				if (selectedLayer) {
					result.push({
						...layer,
						interactivity: selectedLayer.interactivity
					});
				}
				if ((layer.type === 'group') && Array.isArray(layer.layers)) {
					result = result.concat(findSelectedLayers(layer.layers));
				}
			});
			return result;
		}

		return findSelectedLayers(activePreview.layers);
	}

	/**
	 * Gets the selected layer by ID
	 */
	getSelectedLayerById(layerId: string) {
		return this.getValue().interactions.selectedLayers.some(l => l.id === layerId);
	}

	/**
	 * Get the active interaction unit from the preview
	 */

	getActiveInteraction() {
		const activePreview = this.getActivePreview();

		if (activePreview) {
			return activePreview;
		}

		const unitId = this.getValue()?.interactionUnitId;
		return this.getValue().preview.creativeUnitPreviews.find(cu => cu.id === unitId);
	}

	getInteractionSelectedLayers() {
		const activePreview = this.getActiveInteraction();
		return this.getSelectedLayersFromUnitPreview(activePreview);
	}

	getInteractionElement() {
		if (this.getActivePreview()?.id){
			return this.getActivePreviewElement();
		}

		return this.getValue()?.preview?.creativeUnitPreviewElementRefs[this.getValue().interactionUnitId];
	}

	getInteractionLayerElement() {
		let element = this.getValue()?.interactions?.selectedLayers[0]?.element;

		if (!element) {
			let activeItem = this.getActivePreview() ?? this.getActiveInteraction();
			element = this.getValue().preview.creativeUnitPreviewElementRefs[activeItem.id];
		}

		return element;
	}

	getLayerById(layerId: string): LayerDto | undefined {
		const activePreview = this.getActivePreview();
		if (activePreview?.layers?.length) {
			const foundInUnit = LayersUtils.findLayer(activePreview.layers, (layer: LayerDto) => layer.id === layerId);
			if (foundInUnit) {
				return foundInUnit;
			}
		}

		const activePackage = this.creativeUnitPackageQuery.getActive();
		if (activePackage?.layers?.length) {
			const foundInPackage = LayersUtils.findLayer(activePackage.layers, (layer: LayerDto) => layer.id === layerId);
			if (foundInPackage) {
				return foundInPackage;
			}
		}

		return undefined;
	}

	getLayoutGroup(layerId: string): LayoutGroupModel | undefined {
		const layer = this.getLayerById(layerId);
		if (!layer?.group) {
			return undefined;
		}

		const layout = (layer.group as { layout?: Partial<LayoutGroupModel> }).layout;
		if (layout) {
			return normalizeLayoutGroup(layout);
		}

		const fallbackMode =
			layer.group?.flow === 'freeform'
				? LayoutMode.Freeform
				: LayoutMode.FlexHorizontal;

		return createLayoutGroupDefaults({ mode: fallbackMode });
	}

	// ============================================
	// Comment Context Query Methods
	// ============================================

	/**
	 * Gets the current comment context (for layer-linked and timestamp-linked comments)
	 */
	getCommentContext() {
		return this.getValue().commentContext;
	}

	/**
	 * Looks up a layer name by ID. Returns null only if the layer doesn't exist (deleted).
	 * If the layer exists but has no label, returns a fallback based on layer type.
	 * Used for displaying layer references in comments.
	 */
	getLayerName(layerId: string | null): string | null {
		if (!layerId) return null;
		const layer = this.getLayerById(layerId);
		if (!layer) return null;
		// Return label if available, otherwise fall back to layer type
		return layer.label || `${layer.type} layer`;
	}

	/**
	 * Gets a layer by ID for use in comment context operations.
	 * Returns the full layer object or null if not found.
	 */
	getLayerForComment(layerId: string | null): LayerDto | null {
		if (!layerId) return null;
		return this.getLayerById(layerId) ?? null;
	}

	public resolveMergeTagContext(adStudioContainerState?: AdStudioContainerState): Record<string, unknown> | null  {
		const state = adStudioContainerState || this.getValue();
		const activeState = state?.activeCreativeUnitMergeState;
		if (activeState && Object.keys(activeState).length > 0) {
			return activeState;
		}

		const mergedState = state?.mergeTagState;
		if (mergedState && Object.keys(mergedState).length > 0) {
			return mergedState;
		}

		return null;
	}

}
